import {Injectable, inject} from '@angular/core';
import {Observable, catchError, map, of} from 'rxjs';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {Roles} from '../../../_auth/x-enums/roles';
import {CompanyHttpService} from '../../../owner/x-http-requests/company-http.service';
import {SelectableContextKind} from '../../../_auth/context/context.models';
import {GrantScope} from './grant.models';

/** A company the signed-in session may administer grants in. */
export interface GrantTargetCompany {
  id: string;
  name: string;
}

/** A shard the signed-in session may issue a shard-scoped invitation for. */
export interface GrantTargetShard {
  id: string;
  name: string;
}

/**
 * Resolves **what the signed-in session may issue grants for** — which companies, which shards, at which
 * scopes — from the sources each role can actually reach.
 *
 * ### Why this is a service and not three `if`s in the component
 *
 * There is no single "what may I grant here?" endpoint. `GrantIssuancePolicy` decides per request on the
 * server, and the three roles it distinguishes each have a *different* obtainable source for the list:
 *
 * | Session | Companies | Shards | Why that source |
 * | --- | --- | --- | --- |
 * | `PlatformOwner` (no company) | `GET /Management/Company/List` | the `shards` already on each company DTO | that endpoint is `MetaRole.Owner`-gated, i.e. gated to exactly this branch — and its DTO carries the shards, so no second call |
 * | `CompanyOwner` | its own session company | `GET /auth/context/available` | every company-scoped grant hangs off an `ExternalIdentity`, so a `CompanyOwner` session is always an external one and that endpoint answers it; `Management/Shard/List` would 403 |
 * | `ShardAdmin` | its own session company | its own session shard, alone | the policy refuses every other shard anyway, so offering a list would offer refusals |
 *
 * Keeping the branching here means the screen asks one question and the three answers stay independently
 * testable — and it is the one place to change if a dedicated `/grants/scopes` endpoint is ever added.
 */
@Injectable({providedIn: 'root'})
export class GrantScopeService {
  private readonly auth = inject(AuthenticationService);
  private readonly companies = inject(CompanyHttpService);

  private get permissions(): string[] {
    return this.auth.userValue?.permissions ?? [];
  }

  /** True for a platform principal, who is company-independent and may issue anywhere. */
  get isPlatformOwner(): boolean {
    return this.permissions.includes(Roles.PlatformOwner);
  }

  /** True for a company owner (and not also a platform owner, whose branch is broader). */
  get isCompanyOwner(): boolean {
    return !this.isPlatformOwner && this.permissions.includes(Roles.CompanyOwner);
  }

  /**
   * True for a session whose only issuing authority is `ShardAdmin` over its own shard.
   *
   * Deliberately excludes a session that *also* holds `CompanyOwner`/`PlatformOwner`: `GrantIssuancePolicy`
   * grants the wider authority in that case, and narrowing the UI to one shard would hide something the server
   * would have allowed.
   */
  get isShardAdminOnly(): boolean {
    return !this.isPlatformOwner && !this.isCompanyOwner && this.permissions.includes(Roles.ShardAdmin);
  }

  /** Whether this session may reach the screen at all. The server is the real gate; this hides a dead end. */
  get canManageGrants(): boolean {
    return this.isPlatformOwner || this.isCompanyOwner || this.isShardAdminOnly;
  }

  /** The session's own company id, or null for a company-independent platform principal. */
  get sessionCompanyId(): string | null {
    return this.auth.userValue?.company?.id || null;
  }

  /** The session's own shard id, or null when the session carries no shard claim. */
  get sessionShardId(): string | null {
    return this.auth.userValue?.shard?.id || null;
  }

  /**
   * Which scopes this session may issue at.
   *
   * Mirrors `GrantIssuancePolicy.CanIssue`: a shard admin may issue **only** at shard scope — company scope
   * would let it widen its own reach past the shard it administers, up to and including `CompanyOwner` over
   * itself.
   */
  get issuableScopes(): GrantScope[] {
    return this.isShardAdminOnly ? [GrantScope.Shard] : [GrantScope.Company, GrantScope.Shard];
  }

  /**
   * The companies this session may administer.
   *
   * A session that already has a company answers with that one and asks nobody — it is the only company the
   * policy would accept from it anyway. Only the company-independent platform principal fetches a list, and it
   * is the only one allowed to.
   */
  listCompanies(): Observable<GrantTargetCompany[]> {
    const own = this.sessionCompanyId;
    if (own) {
      return of([{id: own, name: this.auth.userValue?.company?.name || own}]);
    }
    if (!this.isPlatformOwner) return of([]);

    return this.companies.list().pipe(
      map(companies => companies
        .map(company => ({id: company.id, name: company.name || company.id}))
        .sort((a, b) => a.name.localeCompare(b.name))),
      // A failed tenant list is a "you cannot pick a company" state, not a broken screen: the caller renders
      // the empty case with a retry rather than letting the error interceptor take over the page.
      catchError(() => of([]))
    );
  }

  /**
   * The shards of `companyId` this session may issue a shard-scoped invitation for.
   *
   * The three branches are the table in the class comment. Each falls back to an empty list rather than an
   * error: an empty shard picker is a state the screen can explain ("no locations available for this choice"),
   * whereas a thrown error here would take down a page whose company-scope half still works.
   */
  listShards(companyId: string): Observable<GrantTargetShard[]> {
    if (!companyId) return of([]);

    if (this.isShardAdminOnly) {
      const shard = this.sessionShardId;
      if (!shard) return of([]);
      return of([{id: shard, name: this.auth.userValue?.shard?.name || shard}]);
    }

    if (this.isPlatformOwner) {
      return this.companies.list().pipe(
        map(companies => {
          const company = companies.find(c => c.id === companyId);
          return (company?.shards ?? [])
            .map(shard => ({id: shard.id, name: shard.name || shard.id}))
            .sort((a, b) => a.name.localeCompare(b.name));
        }),
        catchError(() => of([]))
      );
    }

    // A company owner. `/auth/context/available` lists one entry per (company, shard, view) the account may
    // act in, and `CompanyOwner` cascades into every shard of its company — so the shard contexts of this
    // company ARE its shards. Deduped by id because the same shard appears once per view it opens.
    return this.auth.availableContexts().pipe(
      map(options => {
        const byId = new Map<string, GrantTargetShard>();
        for (const context of options?.contexts ?? []) {
          if (context.kind !== SelectableContextKind.Shard) continue;
          if (context.companyId !== companyId || !context.shardId) continue;
          if (!byId.has(context.shardId)) {
            byId.set(context.shardId, {id: context.shardId, name: context.shardName || context.shardId});
          }
        }
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
      }),
      catchError(() => of([]))
    );
  }
}
